import clsx from 'clsx';
import { type JSX, type ReactElement, useState } from 'react';
import { useController } from 'react-hook-form';
import {
	type ExposedFindingCategoryFilterContext,
	type FindingCategoryFilter,
	FindingCategoryFilterContextProvider,
	InclusionType,
	useFindingCategoryFilterContext
} from 'ts/commons/filters/findings/FindingCategoryFilterContext';
import { MarkdownUtils } from 'ts/commons/markdown/MarkdownUtils';
import { Accordion, AccordionContent, AccordionTitle } from 'ts/components/Accordion';
import { Checkbox } from 'ts/components/Checkbox';
import { Icon } from 'ts/components/Icon';
import { List, ListHeader, ListItem } from 'ts/components/List';
import type { FindingsSummaryCategoryInfo } from 'typedefs/FindingsSummaryCategoryInfo';
import type { FindingsSummaryGroupInfo } from 'typedefs/FindingsSummaryGroupInfo';
import type { FindingsSummaryInfo } from 'typedefs/FindingsSummaryInfo';
import type { FindingsSummaryTypeIdInfo } from 'typedefs/FindingsSummaryTypeIdInfo';
import styles from './CategoryGroupTypeFilter.module.less';

/** Values provided by the CategoryGroupTypeFilterForm component. */
export type CategoryGroupTypeFilterValues = {
	categoryFilter: FindingCategoryFilter;
};

/**
 * This serves as an adapter between the CategoryGroupTypeFilter and the surrounding FormProvider. The component will
 * only work when wrapped in a FormProvider.
 */
export function CategoryGroupTypeFilterForm({
	findingSummary,
	filteredCategoryInfos
}: {
	findingSummary: FindingsSummaryInfo;
	filteredCategoryInfos?: FindingsSummaryCategoryInfo[];
}) {
	const controller = useController<CategoryGroupTypeFilterValues>({ name: 'categoryFilter' });
	return (
		<FindingCategoryFilterContextProvider
			findingSummary={findingSummary}
			findingsCategoryFilter={controller.field.value as FindingCategoryFilter}
			onChange={value => controller.field.onChange(value)}
		>
			<CategoryGroupTypeFilter findingSummary={findingSummary} filteredCategoryInfos={filteredCategoryInfos} />
		</FindingCategoryFilterContextProvider>
	);
}

/**
 * Creates the filters displaying checkboxes for quality indicators, analysis groups, and individual finding types. The
 * component will only work when wrapped in a FindingCategoryFilterContextProvider.
 */
function CategoryGroupTypeFilter({
	findingSummary,
	filteredCategoryInfos
}: {
	findingSummary: FindingsSummaryInfo;
	filteredCategoryInfos?: FindingsSummaryCategoryInfo[];
}): JSX.Element {
	const findingFilter = useFindingCategoryFilterContext();
	const filteredFindingsCount = filteredCategoryInfos?.reduce((acc, current) => acc + current.count, 0);

	return (
		<List className={clsx(styles.checkboxFilters)}>
			<ListItem as={ListHeader} className={styles.subgridAllColumns} data-testid="All-category-item">
				<Checkbox
					id="select-all-checkbox"
					label="All"
					checked={findingFilter.allCategoriesSelected}
					onChange={(_, data) =>
						findingFilter.toggleAllFindingCategories(
							data.checked ? InclusionType.ADD_INCLUDE : InclusionType.ADD_EXCLUDE
						)
					}
				/>
				<FindingCount count={findingFilter.totalFindingCount} filteredCount={filteredFindingsCount} />
			</ListItem>
			{findingSummary.categoryInfos.map(categoryInfo => {
				const filteredCategoryInfo = filteredCategoryInfos?.find(
					info => info.categoryName === categoryInfo.categoryName
				);
				return (
					<CategoryListItem
						key={categoryInfo.categoryName}
						categoryInfo={categoryInfo}
						filteredCategoryInfo={filteredCategoryInfo}
						showFilteredFindingsCount={filteredCategoryInfos !== undefined}
					/>
				);
			})}
		</List>
	);
}

type CategoryListItemProps = {
	categoryInfo: FindingsSummaryCategoryInfo;
	showFilteredFindingsCount: boolean;
	filteredCategoryInfo?: FindingsSummaryCategoryInfo;
};

function CategoryListItem({ categoryInfo, filteredCategoryInfo, showFilteredFindingsCount }: CategoryListItemProps) {
	const findingFilter = useFindingCategoryFilterContext();
	return (
		<>
			<ListItem
				as={ListHeader}
				data-testid={`${categoryInfo.categoryName}-category-item`}
				className={clsx(styles.subgridAllColumns, 'font-bold')}
			>
				<Checkbox
					id={'category-' + categoryInfo.categoryName + '-checkbox'}
					label={categoryInfo.categoryName}
					checked={findingFilter.selectedCategoryNames.includes(categoryInfo.categoryName)}
					onChange={(_, data) =>
						findingFilter.toggleFindingCategory(
							categoryInfo,
							data.checked ? InclusionType.ADD_INCLUDE : InclusionType.ADD_EXCLUDE
						)
					}
				/>
				<FindingCount
					count={categoryInfo.count}
					filteredCount={showFilteredFindingsCount ? (filteredCategoryInfo?.count ?? 0) : undefined}
				/>
			</ListItem>
			<Accordion
				as={ListItem}
				className={clsx(styles.findingCategoryItems, styles.subgridAllColumns)}
				exclusive={false}
			>
				<FindingGroups
					groupInfos={categoryInfo.groupInfos}
					showFilteredFindingsCount={showFilteredFindingsCount}
					filteredCategoryInfo={filteredCategoryInfo}
				/>
			</Accordion>
		</>
	);
}

type FindingGroupsProps = {
	groupInfos: FindingsSummaryGroupInfo[];
	showFilteredFindingsCount: boolean;
	filteredCategoryInfo?: FindingsSummaryCategoryInfo;
};

function FindingGroups({ groupInfos, showFilteredFindingsCount, filteredCategoryInfo }: FindingGroupsProps) {
	const findingFilter = useFindingCategoryFilterContext();

	return (
		<List className={clsx(styles.gridTemplateColumns, styles.gridAllColumns)}>
			{groupInfos.map((findingGroupInfo: FindingsSummaryGroupInfo): JSX.Element => {
				const isEmptyGroup = findingGroupInfo.count === 0;
				const filteredFindingGroupInfo = filteredCategoryInfo?.groupInfos.find(
					info => info.groupName === findingGroupInfo.groupName
				);
				return (
					<ListItem
						key={findingGroupInfo.groupName}
						className={clsx(styles.findingGroup, styles.subgridAllColumns)}
					>
						<FindingGroup
							checkbox={
								<Checkbox
									className={clsx({ [styles.emptyCheckbox!]: isEmptyGroup })}
									id={'group-' + findingGroupInfo.groupName + '-checkbox'}
									checked={findingFilter.selectedGroupNames.includes(findingGroupInfo.groupName)}
									onChange={(_, data) =>
										findingFilter.toggleFindingGroup(
											findingGroupInfo,
											data.checked ? InclusionType.ADD_INCLUDE : InclusionType.ADD_EXCLUDE
										)
									}
								/>
							}
							groupInfo={findingGroupInfo}
							filteredFindingGroupInfo={filteredFindingGroupInfo}
							showFilteredFindingsCount={showFilteredFindingsCount}
						/>
					</ListItem>
				);
			})}
		</List>
	);
}

type FindingGroupTitleProps = {
	groupName: string;
	count: number;
	checkbox: ReactElement;
	filteredCount?: number;
	active?: boolean;
	onClick?: () => void;
};

function FindingGroupTitle({ groupName, count, checkbox, filteredCount, active, onClick }: FindingGroupTitleProps) {
	return (
		<div className={clsx(styles.subgridAllColumns, 'font-semibold')} data-testid={`${groupName}-group-title`}>
			<div className="flex">
				{checkbox}
				<AccordionTitle active={active} onClick={onClick} className="cursor-pointer">
					{count === 0 ? null : <Icon name="dropdown" />}
					<span className={styles.titleText}>{groupName}</span>
				</AccordionTitle>
			</div>
			<FindingCount count={count} filteredCount={filteredCount} />
		</div>
	);
}

type FindingGroupProps = {
	groupInfo: FindingsSummaryGroupInfo;
	filteredFindingGroupInfo?: FindingsSummaryGroupInfo;
	checkbox: ReactElement;
	showFilteredFindingsCount: boolean;
};

function FindingGroup({ groupInfo, filteredFindingGroupInfo, checkbox, showFilteredFindingsCount }: FindingGroupProps) {
	const findingFilter = useFindingCategoryFilterContext();
	const [active, setActive] = useState(() => findingFilter.groupIsPartlySelected(groupInfo));
	const filteredCount = showFilteredFindingsCount ? (filteredFindingGroupInfo?.count ?? 0) : undefined;
	if (groupInfo.count === 0) {
		return (
			<FindingGroupTitle
				groupName={groupInfo.groupName}
				count={groupInfo.count}
				filteredCount={filteredCount}
				checkbox={checkbox}
			/>
		);
	} else {
		return (
			<div
				className={clsx(styles.findingGroupAccordion, styles.subgridAllColumns)}
				data-testid={'group-' + groupInfo.groupName + '-accordion'}
			>
				<FindingGroupTitle
					groupName={groupInfo.groupName}
					count={groupInfo.count}
					filteredCount={filteredCount}
					active={active}
					onClick={() => setActive(active => !active)}
					checkbox={checkbox}
				/>
				<AccordionContent
					active={active}
					className={clsx(styles.gridTemplateColumns, active ? styles.gridAllColumns : '!hidden')}
				>
					<FindingGroupCheckInfo
						groupInfo={groupInfo}
						filteredFindingGroupInfo={filteredFindingGroupInfo}
						findingFilter={findingFilter}
						showFilteredFindingsCount={showFilteredFindingsCount}
					/>
				</AccordionContent>
			</div>
		);
	}
}

type FindingGroupCheckInfoProps = {
	groupInfo: FindingsSummaryGroupInfo;
	filteredFindingGroupInfo?: FindingsSummaryGroupInfo;
	findingFilter: ExposedFindingCategoryFilterContext;
	showFilteredFindingsCount: boolean;
};

function FindingGroupCheckInfo({
	groupInfo,
	filteredFindingGroupInfo,
	findingFilter,
	showFilteredFindingsCount
}: FindingGroupCheckInfoProps): JSX.Element {
	return (
		<>
			{groupInfo.typeIdInfos.map((typeIdInfo: FindingsSummaryTypeIdInfo): JSX.Element => {
				const filteredTypeIdInfoCount =
					filteredFindingGroupInfo?.typeIdInfos.find(info => info.typeIdName === typeIdInfo.typeIdName)
						?.count ?? 0;
				return (
					<div
						className={clsx(styles.findingType, styles.subgridAllColumns)}
						key={typeIdInfo.typeIdName}
						data-testid={`${typeIdInfo.name}-check-item`}
					>
						<Checkbox
							data-testid={'type-' + typeIdInfo.typeIdName + '-checkbox'}
							label={MarkdownUtils.renderToPlainText(typeIdInfo.name)}
							checked={findingFilter.selectedFindingTypeIds.includes(typeIdInfo.typeIdName)}
							onChange={(_, data) =>
								findingFilter.toggleFindingType(
									typeIdInfo,
									data.checked ? InclusionType.ADD_INCLUDE : InclusionType.ADD_EXCLUDE
								)
							}
							className="pr-4"
						/>
						<FindingCount
							count={typeIdInfo.count}
							filteredCount={showFilteredFindingsCount ? filteredTypeIdInfoCount : undefined}
						/>
					</div>
				);
			})}
		</>
	);
}

type FindingCountProps = { count: number; filteredCount?: number };

function FindingCount({ count, filteredCount }: FindingCountProps) {
	return (
		<div data-testid="finding-summaries-count" className={clsx(styles.findingSummariesCount)}>
			{filteredCount !== undefined ? (
				<>
					<span>{filteredCount}</span>
					<span>/</span>
				</>
			) : null}
			<span>{count}</span>
		</div>
	);
}
